/**
 * <popup-signup> — newsletter form on pop-up landing pages (sections/popup-signup.liquid).
 *
 * Posts to the `/subscribe_to_list` API, which (synchronously) ensures a Shopify account,
 * marks it email-subscribed and adds it to the Klaviyo list given in `data-klaviyo-list-id`.
 * The call is idempotent, so a retry after a timeout is safe. No discount code is issued.
 *
 * `data-state` drives the UI: idle → loading → success, or back to idle with an inline error.
 * The result panel is pure CSS keyed off that attribute.
 */
if (!customElements.get('popup-signup')) {
  const API_URL = 'https://api.enroutejewelry.com/subscribe_to_list';
  const TIMEOUT_MS = 20 * 1000;

  /**
   * @param {string} email
   * @param {string} klaviyoListId
   * @returns {Promise<void>} Resolves once the API reports success; rejects otherwise.
   */
  async function subscribeToList(email, klaviyoListId) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, klaviyoListId }),
        signal: controller.signal,
      });
      const result = await response.json();
      if (!result?.data?.success) {
        throw new Error(`[Newsletter] subscribe_to_list failed: ${JSON.stringify(result)}`);
      }
    } finally {
      clearTimeout(timeoutId);
    }

    // Same as the footer flow: tie a guest's on-site Klaviyo activity to this profile.
    if (!window._erj_?.customer?.id) {
      try {
        localStorage.setItem('enroute.klaviyo_email', email);
      } catch {
        // Storage can be blocked (private mode); identification below still works.
      }
      window.klaviyo?.identify?.({ email });
    }
  }

  customElements.define(
    'popup-signup',
    class PopupSignup extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.form = this.querySelector('form');
        this.input = this.form?.elements.email;
        this.button = this.form?.querySelector('button[type="submit"]');
        this.message = this.querySelector('[data-message]');
        if (!this.form || !this.input) return;

        const { signal } = this.abortController;
        this.form.addEventListener('submit', this.onSubmit.bind(this), { signal });
        this.input.addEventListener('input', () => this.clearError(), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
      }

      /** @param {SubmitEvent} event */
      async onSubmit(event) {
        event.preventDefault();
        if (this.dataset.state === 'loading') return;

        const email = this.input.value.trim();
        if (!email || !this.input.checkValidity()) {
          this.showError(this.dataset.invalidMessage);
          this.input.focus();
          return;
        }

        const listId = this.dataset.klaviyoListId;
        if (!listId) {
          console.error('[Newsletter] popup-signup: no Klaviyo list ID configured on the section.');
          this.showError(this.dataset.errorMessage);
          return;
        }

        this.setLoading(true);

        try {
          await subscribeToList(email, listId);
          this.form.reset();
          this.dataset.state = 'success';
          this.querySelector('.popup-signup__result--success')?.focus();
        } catch (error) {
          console.error('[Newsletter] Pop-up sign-up failed:', error);
          this.dataset.state = 'idle';
          this.showError(this.dataset.errorMessage);
        } finally {
          this.setLoading(false);
        }
      }

      /** @param {boolean} loading */
      setLoading(loading) {
        if (loading) this.dataset.state = 'loading';
        this.button?.toggleAttribute('disabled', loading);
        this.button?.setAttribute('aria-busy', String(loading));
      }

      /** @param {string | undefined} text */
      showError(text) {
        this.input.setAttribute('aria-invalid', 'true');
        if (this.message) this.message.textContent = text || '';
      }

      clearError() {
        this.input.removeAttribute('aria-invalid');
        if (this.message) this.message.textContent = '';
      }
    }
  );
}
